import { Link, useNavigate } from '@tanstack/react-router';
import { anzeigeNummer, zweitNummer } from '@/lib/bestellnummer';
import { clsx } from 'clsx';
import {
  COLUMNS,
  formatPreisCent,
  festeBreiteSumme,
  TITEL_MIN_BREITE,
  type ColumnDef
} from '@/lib/dashboardColumns';
import { AgeIndicator } from './AgeIndicator';
import { ZustandMenu } from './ZustandMenu';
import { QuelleIcon } from './QuelleIcon';
import { VersandartIcon } from './VersandartIcon';
import { ErfasserBadge } from './ErfasserBadge';
import { NotizMarke } from './NotizMarke';
import { AbholTermin } from '@/components/dashboard/AbholTermin';
import { NachlieferungMarke } from '@/components/ui/NachlieferungMarke';
import { FortschrittBadge } from '@/components/detail/FortschrittBadge';
import type { BestellungZeile } from '@/types/dashboard';
import { FOKUS_RING_INNEN } from '@/lib/fokus';

type Props = {
  zeilen: BestellungZeile[];
  visibleColumns: string[];
  sort: string;
  onSortChange: (sort: string) => void;
};

const SORTIERBARE_SPALTEN: Record<string, [string, string]> = {
  bestellnr: ['bestellnr_asc', 'bestellnr_desc'],
  kunde: ['kunde_asc', 'kunde_asc'],
  erfasst: ['alter_desc', 'alter_asc']
};

function nextSort(spalte: string, current: string): string {
  const opts = SORTIERBARE_SPALTEN[spalte];
  if (!opts) return current;
  if (current === opts[0]) return opts[1];
  if (current === opts[1]) return 'dringlichkeit';
  return opts[0];
}

function renderCell(col: ColumnDef, b: BestellungZeile): React.ReactNode {
  switch (col.key) {
    case 'bestellnr': {
      // Shop-Bestellungen fuehren die G-Nummer; unsere Bestellnr steht
      // klein darunter, weil sie die interne Identitaet bleibt.
      const zweit = zweitNummer(b);
      // `whitespace-nowrap` an BEIDEN Nummern, nicht nur am Kasten: eine
      // Bestellnummer, die über drei Zeilen läuft, ist keine Nummer mehr,
      // sondern ein Absatz. Genau das war der gemeldete Befund.
      //
      // DER WEG MIT DER TASTATUR (A39, #112). Die Zeile war ein
      // `<tr onClick>` ohne tabIndex: Am Schreibtisch führte nur die Maus
      // in eine Bestellung. Filtern, sortieren, suchen ging mit der
      // Tastatur — die gefundene Zeile öffnen nicht.
      //
      // Der Sprung hängt an DIESER Zelle und nicht an der Zeile. Die
      // Begründung steht im Docblock über `DashboardTable`; kurz: Eine
      // Zeile mit tabIndex und onKeyDown wäre ein Tastatur-Bedienelement
      // mit einem interaktiven Nachfahren — dem Zustands-Chip. Das ist
      // dieselbe Klasse Problem wie der Knopf im Knopf aus #102.
      //
      // Der Link trägt `whitespace-nowrap` und die Schriftfarbe wie
      // vorher das `<span>`: Am Aussehen ändert sich nichts.
      return (
        <Link
          to="/bestellungen/$bestellnr"
          params={{ bestellnr: b.bestellnr }}
          aria-label={`Bestellung ${anzeigeNummer(b)}, ${b.kunde.anzeigename}`}
          // `ring-inset`, nicht der äußere Ring wie auf der Karte: Die
          // Zelle trägt `overflow-hidden` (damit `truncate` greift), und
          // ein außen gezeichneter Ring wäre dort abgeschnitten. Genau so
          // entsteht ein Tab-Stopp, den man nicht sieht — im Browser
          // nachgemessen, nicht aus der Klassenliste geschlossen.
          className={`flex flex-col rounded-sm leading-tight ${FOKUS_RING_INNEN}`}
        >
          <span className="whitespace-nowrap font-serif text-akzent">{anzeigeNummer(b)}</span>
          {zweit && (
            <span className="whitespace-nowrap text-xs text-ink-faint">{zweit}</span>
          )}
        </Link>
      );
    }
    case 'quelle':
      return <QuelleIcon icon={b.bestellweg.icon} label={b.bestellweg.label} />;
    case 'versandart':
      return <VersandartIcon code={b.versandart?.code ?? null} label={b.versandart?.label ?? null} />;
    case 'kunde':
      // Nur noch der Name. Der Titel steht seit A10.1 in einer eigenen
      // Spalte — die gestapelte Zelle machte jede Zeile zweizeilig.
      return (
        <div className="truncate font-medium text-ink" title={b.kunde.anzeigename}>
          {b.kunde.anzeigename}
        </div>
      );
    case 'titel':
      if (!b.haupt_position) return <span className="text-ink-faint">—</span>;
      // `title` als Sprechblase: was abgeschnitten ist, bleibt am
      // Schreibtisch erreichbar, ohne die Zeile höher zu machen. Auf dem
      // Tablet gibt es sie nicht — dort führt der Tipp in die Bestellung,
      // wo der volle Titel steht. Das ist kein Hover-only-Zustand im Sinne
      // der Regel: es geht nichts verloren, was nur so zu erreichen wäre.
      // Die Notiz-Marke sitzt am rechten Rand DIESER Spalte (A12.6) und
      // nicht in einer eigenen: eine Spalte mehr kostet Breite, die bei
      // 1024 px keine ist, und der Titel ist die Rest-Spalte — hier
      // nimmt das Zeichen niemandem etwas weg. Am rechten Rand stehen
      // die Marken untereinander und lassen sich in einem Blick
      // ueberfliegen.
      return (
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="min-w-0 flex-1 truncate" title={b.haupt_position.titel}>
            {b.haupt_position.titel}
          </div>
          {/* DIE MARKE STEHT NEBEN DEM TITEL, NICHT DARIN (A45.3, #122).
              Bis hierher sass sie INNERHALB des abschneidenden Kastens.
              Gemessen im laufenden Bau, Bestellung mit drei Positionen:
              bei kurzem Titel lagen 24,9 von 24,9 Pixeln innerhalb der
              Kante, bei langem Titel 0 von 24,9 — und der Treffertest
              auf den eigenen Mittelpunkt fand sie nicht mehr. Bei 1280
              genauso wie bei 700.

              Wer hinsieht, hielt eine Bestellung mit drei Titeln fuer
              eine mit einem, und zwar ausgerechnet bei langen Titeln:
              Fachbuecher und Reihenbaende. Jetzt gibt der TITEL nach
              (`min-w-0 truncate`), die Marke bleibt (`shrink-0`) — so,
              wie die Notiz-Marke daneben es schon immer machte. */}
          {b.anzahl_positionen > 1 && (
            <span className="shrink-0 italic text-ink-faint">
              + {b.anzahl_positionen - 1}
            </span>
          )}
          {/* A57.1 (#123): „2 von 3 da" — hier und nicht in der
              Zustandsspalte, aus zwei Gruenden.
              INHALTLICH: Die Marke gehoert zu den Titeln. „Drei Titel"
              und „zwei Exemplare davon da" sind eine Aussage; neben
              „3 offen" (noch keinem Beschaffungsweg zugeordnet) stuenden
              zwei Zahlen, die Verschiedenes meinen.
              GEMESSEN: Die Zustandsspalte ist 212 px breit und traegt
              184 px Inhalt (Pille mit Winkelzeichen plus Merkzeichen) —
              der Rest ist Polster. Eine dritte Marke haette dort
              abgeschnitten oder die Spalte um rund 90 px verbreitert,
              und die haetten dem Titel gefehlt. In der Rest-Spalte
              gibt der Titel nach (`min-w-0 truncate`), die Marke bleibt
              (`shrink-0`) — genau wie die beiden Marken daneben es seit
              A45.3 machen. */}
          {b.fortschritt && <FortschrittBadge fortschritt={b.fortschritt} />}
          {/* E52 in der Titel-Zelle, nicht beim Zustand — aus demselben
              Grund wie „2 von 3 da" darüber: gemessen ragte sie in der
              Zustandsspalte 82 px über den Rand (rechte Kante 962 statt
              880) und wäre abgeschnitten worden. Inhaltlich gehört sie
              ohnehin zu den Titeln: Einer davon kommt noch. */}
          {b.nachlieferung && <NachlieferungMarke art={b.nachlieferung} ebene="bestellung" />}
          {(b.hat_bestellnotiz || b.hat_positionsnotiz) && (
            <NotizMarke
              bestellnotiz={b.hat_bestellnotiz}
              positionsnotiz={b.hat_positionsnotiz}
            />
          )}
        </div>
      );
    case 'zustand':
      // Das Merkzeichen sitzt beim Zustand und nicht in einer eigenen
      // Spalte: „wie weit ist sie?“ und „fehlt noch etwas zu bestellen?“
      // sind dieselbe Frage, und eine Spalte mehr kostet auf dem Tablet
      // Breite, die keine hat. Amber, nicht Coral — offene Beschaffung ist
      // Arbeit, kein Fehler.
      // `flex-nowrap` statt `flex-wrap` (A10.1): gemessen brauchten Pille
      // (53 px) und Merkzeichen (49 px) zusammen 108 px, die Spalte bot
      // 108 — es brach genau um, und die Zelle wurde 48 statt 20 px hoch.
      // Das schlug auf JEDE Zeile durch, auch auf die ohne Merkzeichen:
      // eine Tabellenzeile ist so hoch wie ihre hoechste Zelle.
      return (
        <div className="flex flex-nowrap items-center gap-1.5 overflow-hidden">
          <ZustandMenu zeile={b} />
          {b.beschaffung_offen > 0 && (
            <span
              className="shrink-0 whitespace-nowrap rounded bg-gold-soft px-1.5 py-0.5 text-xs tabular-nums text-gold-dark"
              title={`${b.beschaffung_offen} Exemplare sind noch keinem Beschaffungsweg zugeordnet`}
            >
              {b.beschaffung_offen} offen
            </span>
          )}
        </div>
      );
    case 'abholdatum':
      if (!b.abholdatum_zugesagt || b.tage_bis_zugesagt === null) {
        return <span className="text-ink-faint">—</span>;
      }
      // Ist der Termin verstrichen, steht hier derselbe Badge wie in der
      // Detailansicht (B27) — ein Ding, ein Aussehen. Solange er noch
      // bevorsteht, bleibt es beim Punkt mit Restlauf.
      // A57.2 (#123): HIER NENNT DIE MAHNUNG DEN REST NICHT — und das
      // ist eine Entscheidung aus einer Messung, keine Vergesslichkeit.
      //
      // Die Termin-Spalte ist 92 px breit und bietet 68 px Innenraum.
      // Die Mahnung braucht ungebrochen 150,5 px; sie ragt also schon
      // heute um 10 px aus ihrer Zelle, die `overflow:hidden` traegt
      // (Fremdbefund, nicht aus A57). Mit dem Zusatz waeren es 192,8 px
      // und 40 px Ueberstand gewesen: „· 1 fehlt" waere unsichtbar
      // abgeschnitten worden.
      //
      // Der Ausweg ueber `flex-wrap` wurde gemessen und verworfen: Die
      // Mahnung wurde 90 statt 38 px hoch und riss die Zeile von 55 auf
      // 107 px — eine Tabellenzeile ist so hoch wie ihre hoechste Zelle
      // (die Lehre aus A10.1), und das haette JEDE Zeile getroffen.
      //
      // Die Spalte zu verbreitern kostete 125 px, die bei 1024 der
      // Titel-Spalte fehlen — die steht dort schon auf ihrem Mindestmass
      // von 120 px. Zu teuer fuer eine Angabe, die in derselben ZEILE
      // ohnehin steht: Die Marke „2 von 3 da" in der Titel-Zelle sagt
      // genau dasselbe, und dort ist Platz dafuer.
      return <AbholTermin zeile={b} restOffen={null} />;
    case 'erfasst':
      return <AgeIndicator tage={b.tage_seit_erfasst} modus="seit" datum={b.erfasst_am} />;
    case 'erfasser':
      return <ErfasserBadge user={b.erfasser} />;
    case 'anzahl':
      return <span className="tabular-nums">{b.anzahl_positionen}</span>;
    case 'preis':
      return <span className="tabular-nums">{formatPreisCent(b.preis_summe_cent)}</span>;
    case 'verlag':
      return <span>{b.haupt_position?.verlag_label ?? '—'}</span>;
    default:
      return null;
  }
}

/**
 * Das Dashboard am Schreibtisch.
 *
 * DER WEG MIT DER TASTATUR (A39, #112)
 *   Fremdbefund aus A33: Die Zeile war ein `<tr onClick>` — kein
 *   tabIndex, kein onKeyDown. Am Schreibtisch führte damit nur die Maus
 *   in eine Bestellung. Filtern, sortieren, suchen ging mit der
 *   Tastatur; die gefundene Zeile öffnen nicht. Auf der Karte am Telefon
 *   ist es seit A33 in Ordnung — dort ist die Fläche ein <Link>. Die
 *   Tabelle war die verbliebene Hälfte.
 *
 * WARUM EIN LINK IN DER NUMMERN-ZELLE UND NICHT `<tr tabIndex onKeyDown>`
 *   Die Zeile enthält den Zustands-Chip, ein echtes Bedienelement. Eine
 *   Zeile mit tabIndex und onKeyDown wäre damit ein
 *   Tastatur-Bedienelement mit einem interaktiven Nachfahren — dieselbe
 *   Klasse Problem wie der Knopf im Knopf aus #102, nur ohne die
 *   DOM-Warnung, die uns dort geholfen hat.
 *
 *   Konkret und nicht bloß grundsätzlich: Enter auf dem fokussierten
 *   Chip öffnet dessen Menü, und das keydown steigt in die Zeile auf,
 *   die daraufhin navigiert. Man müsste das mit stopPropagation
 *   zuhalten — also die Verschachtelung kaschieren statt sie zu
 *   vermeiden. Genau diesen Tausch hat A33 auf der Karte verworfen.
 *
 *   Der Link bringt außerdem mit, was ein tabIndex nie hat: Adresse in
 *   der Statusleiste, Öffnen in neuem Tab, Mittelklick. Und die
 *   Vorlesereihenfolge stimmt — die Kennung der Zeile ist das Erste, was
 *   angesagt wird.
 *
 * GEZÄHLT, NICHT GESCHÄTZT (A39.1)
 *   Vorher 1 Tab-Stopp je Zeile (der Chip), nachher 2 (Link, dann Chip).
 *   Ein zweiter Weg in dieselbe Bestellung — etwa zusätzlich `tabIndex`
 *   an der Zeile — machte daraus 3, und bei fünfzig Zeilen ist
 *   Durchtabben dann schlechter als vorher, nicht besser. Eine
 *   Zusicherung hält die Zahl fest.
 *
 * DIE MAUS ÄNDERT SICH NICHT: Ein Klick irgendwo in die Zeile öffnet
 * weiterhin die Bestellung.
 */
export function DashboardTable({ zeilen, visibleColumns, sort, onSortChange }: Props) {
  const navigate = useNavigate();
  const aktiveCols = COLUMNS.filter((c) => visibleColumns.includes(c.key));

  // Der Anker sitzt am Behälter und nicht an der Tabelle: bei leerem
  // Ergebnis gab es ihn früher gar nicht, und die Tour hätte bei einem
  // strengen Filter ins Leere gezeigt. Die Kartenansicht hält es genauso.
  if (zeilen.length === 0) {
    return (
      <div
        data-tour="dashboard-tabelle"
        className="border-t border-line p-12 text-center text-sm text-ink-faint"
      >
        Keine Bestellungen gefunden.
      </div>
    );
  }

  const festeSumme = festeBreiteSumme(aktiveCols);
  const hatRestSpalte = aktiveCols.some((c) => c.breite === null);

  return (
    // KEIN `overflow-x-auto` MEHR (A10.2)
    //
    // Es stand hier, und es war der Grund, warum der Spaltenkopf beim
    // Rollen verschwand, obwohl `sticky top-0` an ihm hing. `overflow-x`
    // macht aus diesem <div> einen eigenen Roll-Kasten — auch senkrecht,
    // denn ein sichtbares `overflow-y` ist neben einem gerollten
    // `overflow-x` nicht möglich. Der Kopf klebte damit an der Oberkante
    // eines Kastens, der selbst mitrollte, und war nach 1988 px Rollen
    // 1861 px über dem Fensterrand.
    //
    // Gerollt wird jetzt im Kasten darüber (routes/index.tsx), und der
    // rollt in beide Richtungen. Daran kann der Kopf wirklich haften.
    <div data-tour="dashboard-tabelle">
      <table
        className="w-full table-fixed text-sm"
        // `table-fixed` ist der zweite Teil des Fixes: in automatischem
        // Layout sind Breitenangaben nur Vorschläge, und der Browser gab
        // der Nr.-Spalte 63 px statt der angesetzten 112. Mit festem
        // Layout gilt, was im <colgroup> steht.
        style={
          hatRestSpalte ? { minWidth: `${festeSumme + TITEL_MIN_BREITE}px` } : undefined
        }
      >
        <colgroup>
          {aktiveCols.map((c) => (
            <col key={c.key} style={c.breite === null ? undefined : { width: `${c.breite}px` }} />
          ))}
        </colgroup>
        {/* E58: Kopf kräftiger und mit deutlicher Unterkante — er trennt
            Überschrift und Daten, ohne ein Gitter aufzuspannen. */}
        <thead className="sticky top-0 z-10 bg-kopf">
          <tr className="border-b-2 border-feldrand/50">
            {/* data-tour am Spaltenkopf und nicht am Überfällig-Merkzeichen
                selbst: das gibt es nur, wenn gerade etwas überfällig IST,
                und ein Anker muss verlässlich da sein. */}
            {aktiveCols.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={clsx(
                  'truncate py-2 text-left font-medium text-ink',
                  // Enge Polsterung nur bei den schmalen Symbol-Spalten
                  // (A12.12) — sonst passt ihr Kopftext nicht.
                  c.kopfEng ? 'px-1' : 'px-3',
                  c.align === 'right' && 'text-right',
                  c.align === 'center' && 'text-center',
                  SORTIERBARE_SPALTEN[c.key] && 'cursor-pointer hover:text-akzent'
                )}
                onClick={() => SORTIERBARE_SPALTEN[c.key] && onSortChange(nextSort(c.key, sort))}
                data-tour={c.key === 'abholdatum' ? 'dashboard-abholung' : undefined}
                title={c.tooltip}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((b) => (
            <tr
              key={b.id}
              // Bequemlichkeit für die MAUS, unverändert: Ein Klick
              // irgendwo in die Zeile öffnet die Bestellung. Kein
              // tabIndex, kein onKeyDown — der Weg mit der Tastatur führt
              // über den Link in der Nummern-Zelle (A39, #112).
              //
              // Der Ausstieg oben ist neu und nötig: Ohne ihn liefe beim
              // Klick auf den Link BEIDES — der Link navigiert, und der
              // Zeilen-Klick navigiert gleich noch einmal. Der
              // Zustands-Chip hält seinen Klick schon selbst auf
              // (`stopPropagation` in ZustandMenu); dass hier trotzdem
              // auf `button` geprüft wird, ist Absicht: Die Zeile soll
              // nicht davon abhängen, dass jedes künftige Bedienelement
              // daran denkt.
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a, button')) return;
                // Strg-/Cmd-Klick: in einem neuen Fenster, wie bei einem
                // Link (Befund 02.11.2026 — „Tabs" am PC). Die Nummer
                // selbst ist ohnehin ein echter Link (A39).
                if (e.ctrlKey || e.metaKey) {
                  window.open(`/bestellungen/${encodeURIComponent(b.bestellnr)}`, '_blank', 'noopener');
                  return;
                }
                void navigate({
                  to: '/bestellungen/$bestellnr',
                  params: { bestellnr: b.bestellnr }
                });
              }}
              onAuxClick={(e) => {
                // Mittelklick: ebenfalls ein neues Fenster.
                if (e.button !== 1 || (e.target as HTMLElement).closest('a, button')) return;
                e.preventDefault();
                window.open(`/bestellungen/${encodeURIComponent(b.bestellnr)}`, '_blank', 'noopener');
              }}
              // E58: Zebra-Streifen fürs Lesen über die Zeile (Ji et al., IEEE
              // VIS 2024) und die ganze Zeile in der Nutzerfarbe unter dem
              // Zeiger, mit Balken links (Carbon: Zeilen-Hover immer an).
              className="cursor-pointer border-b border-line bg-cream even:bg-cream-dark hover:bg-akzent-sanft hover:shadow-[inset_3px_0_0_rgb(var(--akzent))]"
            >
              {aktiveCols.map((c) => (
                <td
                  key={c.key}
                  data-tour={c.key === 'zustand' ? 'dashboard-zustand' : undefined}
                  className={clsx(
                    // `align-middle` statt `align-top`: die Zeilen sind
                    // seit A10.1 einzeilig, und dann sieht oben bündig
                    // aus wie verrutscht. `overflow-hidden` sorgt dafür,
                    // dass `truncate` in den Zellen überhaupt greifen
                    // kann — ohne es läuft der Text über die Spalte
                    // hinaus, statt abgeschnitten zu werden.
                    'overflow-hidden px-3 py-2 align-middle',
                    c.nowrap && 'whitespace-nowrap',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center'
                  )}
                >
                  {renderCell(c, b)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
